import React, { useState } from "react";
import { X } from "lucide-react";

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "919696960998";
const DEFAULT_MSG =
  "Hi Nuvun team, I'm exploring your prefabricated living platform and would like to talk to a specialist.";

const WA_LINK = (msg = DEFAULT_MSG) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

const QUICK_PROMPTS = [
  "I'm a landowner exploring a weekend home / villa.",
  "I'm a resort / retreat developer — need inventory.",
  "I want to download the full catalogue.",
  "I have a specific product in mind — let's discuss.",
];

// Authentic WhatsApp glyph
const WhatsAppGlyph = ({ size = 28, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="currentColor"
    aria-hidden="true"
    className={className}
  >
    <path d="M19.11 17.21c-.27-.13-1.57-.77-1.81-.86-.24-.09-.42-.13-.6.13-.18.27-.69.86-.84 1.04-.16.18-.31.2-.58.07-.27-.13-1.13-.42-2.16-1.33-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.42.12-.55.12-.12.27-.31.4-.47.13-.16.18-.27.27-.45.09-.18.04-.34-.02-.47-.07-.13-.6-1.45-.82-1.99-.22-.52-.44-.45-.6-.46l-.51-.01c-.18 0-.47.07-.71.34-.24.27-.93.91-.93 2.22 0 1.31.95 2.57 1.08 2.75.13.18 1.87 2.85 4.53 3.99.63.27 1.13.43 1.51.55.63.2 1.21.17 1.67.1.51-.08 1.57-.64 1.79-1.26.22-.62.22-1.16.16-1.27-.07-.11-.24-.18-.51-.31z"/>
    <path d="M26.55 5.43A14.93 14.93 0 0 0 16 1.07C7.74 1.07 1.07 7.74 1.07 16c0 2.63.69 5.21 2 7.47L1 30.93l7.62-2A14.86 14.86 0 0 0 16 30.93h.01c8.26 0 14.93-6.67 14.93-14.93 0-3.99-1.55-7.74-4.39-10.57zM16 28.43h-.01a12.42 12.42 0 0 1-6.32-1.73l-.45-.27-4.52 1.19 1.21-4.41-.29-.46A12.4 12.4 0 0 1 3.57 16C3.57 9.12 9.12 3.57 16 3.57c3.32 0 6.44 1.29 8.79 3.64A12.36 12.36 0 0 1 28.43 16c0 6.88-5.55 12.43-12.43 12.43z"/>
  </svg>
);

export default function WhatsAppFab() {
  const [open, setOpen] = useState(false);

  return (
    <div className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-40 print:hidden flex flex-col items-end pointer-events-none" data-testid="whatsapp-fab">
      {/* Expanded panel */}
      <div
        className={`mb-3 transition-all duration-300 origin-bottom-right ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        {/* Chat card (compact, reference-style) */}
        <div className="relative bg-black border-2 border-[#EEBF04] rounded-full pl-2 pr-5 py-2 flex items-center gap-3 shadow-[0_0_24px_-4px_rgba(238,191,4,0.55)] w-[300px] max-w-[calc(100vw-2.5rem)]">
          <span className="relative inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#25D366] text-white shrink-0 ring-2 ring-[#EEBF04] shadow-[0_0_18px_-2px_rgba(238,191,4,0.7)]">
            <WhatsAppGlyph size={26} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-display text-white text-base leading-none">Chat with NUVUN</div>
            <div className="font-body text-[11px] text-white/70 mt-1">We&apos;re online to help!</div>
          </div>
          <button
            onClick={() => setOpen(false)}
            data-testid="whatsapp-fab-close"
            className="absolute -top-2 -right-2 w-6 h-6 bg-[#EEBF04] text-black rounded-full inline-flex items-center justify-center hover:bg-white"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Prompts (only on expand) */}
        <div className="mt-2 bg-black border border-[#EEBF04]/40 p-3 space-y-2 w-[300px] max-w-[calc(100vw-2.5rem)]">
          <div className="flex items-center justify-between">
            <span className="eyebrow text-[#EEBF04] text-[10px]">Pick a context</span>
            <span className="inline-flex items-center gap-1.5 text-[10px] text-white/70">
              <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] animate-pulse" />
              Online
            </span>
          </div>
          {QUICK_PROMPTS.map((p, i) => (
            <a
              key={i}
              href={WA_LINK(p)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`whatsapp-prompt-${i}`}
              className="block font-body text-xs text-white border border-white/15 px-3 py-2.5 hover:border-[#EEBF04] hover:bg-[#EEBF04]/10 transition-colors"
            >
              {p}
            </a>
          ))}
          <a
            href={WA_LINK()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-open-chat"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-2.5 hover:bg-[#1ebe57] transition-colors"
          >
            <WhatsAppGlyph size={16} />
            <span className="eyebrow text-[10px]">Open WhatsApp</span>
          </a>
        </div>
      </div>

      {/* FAB — green WhatsApp button with neon yellow glow ring (reference image) */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="whatsapp-fab-toggle"
        aria-label="Talk to a specialist on WhatsApp"
        className="group relative w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#25D366] text-white flex items-center justify-center transition-all duration-300 hover:scale-105 pointer-events-auto"
        style={{
          boxShadow:
            "0 0 0 3px rgba(238,191,4,0.95), 0 0 24px 4px rgba(238,191,4,0.55), 0 10px 30px -5px rgba(37,211,102,0.5)",
        }}
      >
        {/* Outer pulsing glow */}
        <span className="absolute inset-[-6px] rounded-full border-2 border-[#EEBF04]/70 animate-ping opacity-60 pointer-events-none" />
        <WhatsAppGlyph size={28} className="relative z-10" />
        {/* Online dot */}
        <span className="absolute -bottom-0 -right-0 w-3.5 h-3.5 rounded-full bg-[#25D366] border-2 border-black shadow-[0_0_6px_rgba(37,211,102,0.9)]" />
      </button>
    </div>
  );
}
